/**
 * Floating WhatsApp Button
 * ─────────────────────────────────────────────────────────────────────────────
 * Handles welcome-bubble lifecycle:
 *   • sessionStorage gate (show-once option)
 *   • Waits for CSS fade-in to complete, then starts the hold timer
 *   • Fades out and removes the bubble from the DOM
 *
 * Requirements:
 *   • Vanilla JS — no jQuery, no external libraries
 *   • No inline event handlers
 *   • Works with prefers-reduced-motion
 */

(function () {
  'use strict';

  /** sessionStorage key used to track whether the bubble has been shown. */
  var SESSION_KEY = 'fwa_bubble_shown';

  /**
   * localStorage key used to persist the collapsed/dismissed state across
   * page reloads. Unlike the bubble (session-only), the dismissed state
   * should survive refreshes so the button does not keep reappearing over
   * the mobile navigation bar.
   */
  var DISMISSED_KEY = 'fwa_dismissed';

  /**
   * Entry point — reads configuration from data attributes on the wrapper
   * element and delegates to feature-specific handlers.
   */
  function init() {
    var wrapper = document.getElementById('floating-whatsapp');
    if (!wrapper) return;

    var bubbleEnabled = wrapper.dataset.bubbleEnabled === 'true';
    var bubbleOnce    = wrapper.dataset.bubbleOnce    === 'true';
    // Convert seconds (from schema range) to milliseconds for setTimeout
    var bubbleDuration = parseFloat(wrapper.dataset.bubbleDuration || '5') * 1000;

    if (bubbleEnabled) {
      handleBubble(bubbleOnce, bubbleDuration);
    }

    handleCollapsible(wrapper);
  }

  /**
   * Manages the collapse/restore lifecycle.
   *
   * • On init: restores the last known state from localStorage.
   * • Close button: collapses the wrapper and persists the state.
   * • Restore button: expands the wrapper and clears the persisted state.
   *
   * localStorage is intentionally used (vs sessionStorage) so the button
   * stays dismissed across page reloads — especially important on mobile
   * where the button can overlap sticky bottom navigation.
   *
   * @param {HTMLElement} wrapper
   */
  function handleCollapsible(wrapper) {
    var closeBtn   = document.getElementById('fwa-close');
    var restoreBtn = document.getElementById('fwa-restore');

    if (!closeBtn || !restoreBtn) return;

    // Restore previous collapsed state (silently, before paint)
    try {
      if (localStorage.getItem(DISMISSED_KEY)) {
        collapseWrapper(wrapper);
      }
    } catch (_) {
      // localStorage unavailable — non-critical, continue without persistence
    }

    closeBtn.addEventListener('click', function () {
      collapseWrapper(wrapper);
      try { localStorage.setItem(DISMISSED_KEY, '1'); } catch (_) {}
    });

    restoreBtn.addEventListener('click', function () {
      expandWrapper(wrapper);
      try { localStorage.removeItem(DISMISSED_KEY); } catch (_) {}
    });
  }

  /**
   * Collapses the wrapper, hiding the bubble and main button.
   * @param {HTMLElement} wrapper
   */
  function collapseWrapper(wrapper) {
    wrapper.classList.add('fwa-wrapper--collapsed');
  }

  /**
   * Expands the wrapper back to its full state.
   * @param {HTMLElement} wrapper
   */
  function expandWrapper(wrapper) {
    wrapper.classList.remove('fwa-wrapper--collapsed');
  }

  /**
   * Manages the welcome-bubble lifecycle.
   *
   * Flow:
   *  1. If show-once is active and the bubble was already shown this session →
   *     remove the element immediately (before the CSS animation fires).
   *  2. Otherwise, mark the session if required, wait for the CSS fade-in to
   *     complete, hold for `duration` ms, then fade out and remove from DOM.
   *
   * @param {boolean} once     - Whether to show only once per browser session.
   * @param {number}  duration - How long (ms) to keep the bubble visible after
   *                             the fade-in animation completes.
   */
  function handleBubble(once, duration) {
    var bubble = document.getElementById('fwa-bubble');
    if (!bubble) return;

    // ── Session gate ────────────────────────────────────────────────────────
    if (once) {
      try {
        if (sessionStorage.getItem(SESSION_KEY)) {
          // Already shown — remove silently before animation starts
          bubble.remove();
          return;
        }
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch (_) {
        // sessionStorage unavailable (private mode quirks on some browsers).
        // Fall through and show the bubble anyway — non-critical.
      }
    }

    // ── Wait for CSS fade-in, then schedule hide ─────────────────────────────
    //
    // The bubble's CSS animation has a 0.55 s delay + 0.35 s duration = ~0.9 s
    // total before it is fully visible. We listen for `animationend` to start
    // the hold timer at the correct moment.
    //
    // Fallback timer (1 200 ms) ensures the hold timer still starts even when
    // the animation is suppressed by `prefers-reduced-motion`.

    var holdStarted = false;

    function startHoldTimer() {
      if (holdStarted) return;
      holdStarted = true;
      setTimeout(function () {
        hideBubble(bubble);
      }, duration);
    }

    bubble.addEventListener('animationend', startHoldTimer, { once: true });

    // Fallback: 550 ms delay + 350 ms anim + 300 ms buffer = 1 200 ms
    setTimeout(startHoldTimer, 1200);
  }

  /**
   * Fades out the bubble by adding the hide class, then removes it from the
   * DOM once the CSS transition completes.
   *
   * @param {HTMLElement} bubble
   */
  function hideBubble(bubble) {
    if (!bubble.isConnected) return;

    bubble.classList.add('fwa-bubble--hiding');

    function onFadeOutEnd() {
      if (bubble.isConnected) bubble.remove();
    }

    bubble.addEventListener('animationend', onFadeOutEnd, { once: true });

    // Fallback removal: 300 ms (fade-out duration) + 300 ms buffer
    setTimeout(onFadeOutEnd, 600);
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  //
  // The script is loaded with `defer`, so the DOM is parsed by the time it
  // executes. We check readyState as a defensive measure for edge cases where
  // the script might run before DOMContentLoaded.

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
